import * as React from 'react';
import { ISize } from '@uifabric/utilities/lib/ISize';
import { itemThumbnailControl } from '../../controls/itemThumbnailControl';
import { css } from '@uifabric/utilities/lib/css';
import { fitContentToBounds } from '@uifabric/utilities/lib/math';
import * as ItemThumbnailStyles from './ItemThumbnail.scss';
import { thumbnailFacet, dimensionsFacet } from '../../facets/MediaFacets';
import { imageByUrlKeyFacet } from '../../models/images/ImageByUrlKeyFacet';
import { openUrlFacet } from '../../facets/ItemFacets';
import { imageAddon } from '../../operations/images/ImageAddon';
import { IItemCacheDispatchProp, connectToItemCache } from '../../components/decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IDecoratedProps } from '../../utilities/components/Decorators';

export interface IOnThumbnailLoadProps { }
export interface IOnThumbnailErrorProps { }

export interface IThumbnailBaseProps {
  url?: string;
  actualSize?: ISize;
  desiredSize: ISize;
  className?: string;
  onLoad?: (props: IOnThumbnailLoadProps) => void;
  onError?: (props: IOnThumbnailErrorProps) => void;
}

export type IItemThumbnailProps = IDecoratedProps<IThumbnailBaseProps, Pick<IThumbnailBaseProps, 'url'>, {
  itemKey: string;
  desiredSize: ISize;
}>;

class ThumbnailBase extends React.PureComponent<IThumbnailBaseProps & IItemCacheDispatchProp> {
  public componentDidMount(): void {
    this.props.itemCacheDispatch(imageAddon);
  }

  public render(): JSX.Element | null {
    const {
      url,
      actualSize,
      desiredSize,
      className,
      onError,
      onLoad
    } = this.props;

    if (!url) {
      return null;
    }

    let finalSize: ISize | undefined;

    if (isValidSize(actualSize) && isValidSize(desiredSize)) {
      finalSize = fitContentToBounds({
        boundsSize: desiredSize,
        contentSize: actualSize,
        mode: 'contain'
      });
    }

    return (
      <img
        className={ css(ItemThumbnailStyles.image, className) }
        src={ url }
        onLoad={ onLoad }
        onError={ onError }
        { ...(finalSize ? {
          style: {
            width: `${finalSize.width}px`,
            height: `${finalSize.height}px`
          }
        } : {}) }
      />
    );
  }
}

export const ItemThumbnail: React.ComponentType<IItemThumbnailProps> = connectToItemCache((
  itemCache: IItemCache,
  props: Pick<IItemThumbnailProps, 'itemKey' | 'desiredSize'>
): Pick<IThumbnailBaseProps, 'url' | 'actualSize'> => {
  const {
    itemKey,
    desiredSize
  } = props;

  let url: string | undefined;
  let actualSize: ISize | undefined;

  const isThumbnailResolved = itemCache.isItemFacetResolved(thumbnailFacet, itemKey);
  const image = isThumbnailResolved ? itemCache.demandItemFacet(thumbnailFacet, itemKey) : undefined;

  if (!isThumbnailResolved || image) {
    const controlHandler = itemCache.demandItemFacet(itemThumbnailControl, itemKey);

    const {
      thumbnail
    } = itemThumbnailControl.evaluate(controlHandler)(itemCache, {
      itemKey: itemKey,
      originalImage: image,
      desiredSize: desiredSize,
      thumbnail: image
    });

    if (thumbnail && thumbnail.url) {
      const imageItemKey = imageByUrlKeyFacet.serialize({
        url: thumbnail.url
      });

      // Wrap the image with a request using the image load subsystem.
      url = itemCache.demandItemFacet(openUrlFacet, imageItemKey);
      actualSize = itemCache.demandItemFacet(dimensionsFacet, imageItemKey);
    }
  }

  return {
    url,
    actualSize
  };
})(ThumbnailBase);

function isValidSize(size: ISize | undefined): size is ISize {
  return !!size && size.width > 0 && size.height > 0;
}
